// Step-up re-auth modal for admin mutating actions.
// Spec: docs/plans/admin-tooling.md D-62

import { useState, useEffect } from 'react';
import { AlertTriangle, ShieldCheck } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { b64urlToUint8Array, serializeAssertionResponse } from '@/lib/webauthn';
import { getIntentLabel, isDestructiveIntent } from '@/lib/admin/intent-labels';

// ── Types ─────────────────────────────────────────────────────────────────────

interface WebAuthnAllowCredential {
  type: string;
  id: string;
  transports?: AuthenticatorTransport[];
}

interface WebAuthnOptions {
  challenge: string;
  timeout: number;
  rpId: string;
  allowCredentials: WebAuthnAllowCredential[];
  userVerification: UserVerificationRequirement;
}

interface StepUpInitResponse {
  ok: boolean;
  challenge_id?: string;
  requires?: 'webauthn' | 'totp';
  options?: WebAuthnOptions | null;
  error?: string;
}

interface StepUpVerifyResponse {
  ok: boolean;
  stepup_token?: string;
  error?: string;
}

export interface StepUpModalProps {
  actionIntent: string;
  targetUserId: number | null;
  onVerified: (token: string) => void;
  onCancel: () => void;
}

// ── State machine ─────────────────────────────────────────────────────────────

type Phase = 'init' | 'webauthn' | 'totp' | 'done' | 'error';

// ── Intent banner ─────────────────────────────────────────────────────────────

function IntentBanner({
  actionIntent,
  targetUserId,
}: {
  actionIntent: string;
  targetUserId: number | null;
}) {
  const label = getIntentLabel(actionIntent);
  const destructive = isDestructiveIntent(actionIntent);

  if (destructive) {
    return (
      <div className="flex items-start gap-3 rounded-sm border border-destructive border-l-2 border-l-destructive bg-destructive-soft/30 px-3 py-2.5">
        <AlertTriangle size={16} className="mt-0.5 shrink-0 text-destructive-strong" aria-hidden="true" />
        <div>
          <p className="text-sm font-semibold text-destructive-strong">
            Authorizing: {label}
          </p>
          {targetUserId !== null && (
            <p className="text-xs text-ink-mid font-mono tabular-nums">Player #{targetUserId}</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-3 rounded-sm border border-border border-l-2 border-l-primary bg-primary-soft/30 px-3 py-2.5">
      <ShieldCheck size={16} className="mt-0.5 shrink-0 text-primary" aria-hidden="true" />
      <div>
        <p className="text-sm font-semibold text-ink-high">
          Authorizing: {label}
        </p>
        {targetUserId !== null && (
          <p className="text-xs text-ink-mid font-mono tabular-nums">Player #{targetUserId}</p>
        )}
      </div>
    </div>
  );
}

// ── ModalShell ────────────────────────────────────────────────────────────────

function ModalShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/85">
      <div className="relative w-full max-w-sm mx-4 rounded-sm border border-border bg-panel-elev p-5 shadow-[0_24px_48px_rgba(0,0,0,0.4)] space-y-4">
        {children}
      </div>
    </div>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export function StepUpModal({ actionIntent, targetUserId, onVerified, onCancel }: StepUpModalProps) {
  const [phase, setPhase] = useState<Phase>('init');
  const [challengeId, setChallengeId] = useState('');
  const [_requires, setRequires] = useState<'webauthn' | 'totp' | null>(null);
  const [_webAuthnOptions, setWebAuthnOptions] = useState<WebAuthnOptions | null>(null);
  const [totpCode, setTotpCode] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [busy, setBusy] = useState(false);

  // Run init on mount (and on retry)
  const runInit = () => {
    setPhase('init');
    setErrorMsg('');
    setTotpCode('');
    setBusy(true);

    void (async () => {
      const res = await apiFetch<StepUpInitResponse>('/admin/auth/stepup/init', {
        method: 'POST',
        body: JSON.stringify({ action_intent: actionIntent, target_user_id: targetUserId }),
      });

      setBusy(false);

      if (!res.ok || !res.data.ok) {
        const status = res.status;
        if (status === 429) {
          setErrorMsg('Too many attempts. Please wait and try again.');
        } else {
          setErrorMsg(res.data.error ?? 'Step-up authentication failed. Please try again.');
        }
        setPhase('error');
        return;
      }

      const { challenge_id, requires: req, options } = res.data;
      if (!challenge_id || !req) {
        setErrorMsg('Invalid response from server.');
        setPhase('error');
        return;
      }

      setChallengeId(challenge_id);
      setRequires(req);

      if (req === 'webauthn') {
        setWebAuthnOptions(options ?? null);
        setPhase('webauthn');
        // kick off WebAuthn immediately
        runWebAuthn(challenge_id, options ?? null);
      } else {
        setPhase('totp');
      }
    })();
  };

  const runWebAuthn = (cid: string, opts: WebAuthnOptions | null) => {
    if (!opts) {
      setErrorMsg('Missing WebAuthn options from server.');
      setPhase('error');
      return;
    }

    setBusy(true);

    void (async () => {
      let cred: PublicKeyCredential;
      try {
        const toBuffer = (s: string): ArrayBuffer => b64urlToUint8Array(s).buffer as ArrayBuffer;

        const result = await navigator.credentials.get({
          publicKey: {
            challenge: toBuffer(opts.challenge),
            timeout: opts.timeout,
            rpId: opts.rpId,
            allowCredentials: opts.allowCredentials.map((c) => ({
              type: c.type as PublicKeyCredentialType,
              id: toBuffer(c.id),
              ...(c.transports !== undefined ? { transports: c.transports } : {}),
            })),
            userVerification: opts.userVerification,
          },
        });

        if (!result || result.type !== 'public-key') {
          throw new Error('No credential returned.');
        }

        cred = result as PublicKeyCredential;
      } catch (e) {
        setBusy(false);
        const msg = e instanceof Error ? e.message : 'WebAuthn failed.';
        setErrorMsg(msg);
        setPhase('error');
        return;
      }

      const assertionResponse = serializeAssertionResponse(cred);

      const res = await apiFetch<StepUpVerifyResponse>('/admin/auth/stepup/verify', {
        method: 'POST',
        body: JSON.stringify({ challenge_id: cid, assertion_response: assertionResponse }),
      });

      setBusy(false);

      if (!res.ok || !res.data.ok || !res.data.stepup_token) {
        setErrorMsg(res.data.error ?? 'WebAuthn verification failed.');
        setPhase('error');
        return;
      }

      setPhase('done');
      onVerified(res.data.stepup_token);
    })();
  };

  const submitTotp = () => {
    if (totpCode.length !== 6) return;
    setBusy(true);

    void (async () => {
      const res = await apiFetch<StepUpVerifyResponse>('/admin/auth/stepup/verify', {
        method: 'POST',
        body: JSON.stringify({ challenge_id: challengeId, totp_code: totpCode }),
      });

      setBusy(false);

      if (!res.ok || !res.data.ok || !res.data.stepup_token) {
        setErrorMsg(res.data.error ?? 'Invalid code. Please try again.');
        setPhase('error');
        return;
      }

      setPhase('done');
      onVerified(res.data.stepup_token);
    })();
  };

  useEffect(() => {
    runInit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <ModalShell>
      {/* Intent banner — visible in EVERY phase */}
      <IntentBanner actionIntent={actionIntent} targetUserId={targetUserId} />

      <h2 className="text-base font-semibold text-ink-high">
        Re-authenticate to authorize this action
      </h2>

      {/* Init / WebAuthn tap spinner */}
      {(phase === 'init' || phase === 'webauthn') && (
        <div className="flex flex-col items-center gap-3 py-4">
          <div className="h-7 w-7 animate-spin rounded-full border-4 border-ink-low border-t-primary" />
          <p className="text-sm text-ink-mid">
            {phase === 'init' ? 'Initializing…' : 'Tap your security key…'}
          </p>
        </div>
      )}

      {/* TOTP input */}
      {phase === 'totp' && (
        <div className="space-y-3">
          <label className="block text-xs font-medium uppercase tracking-wider text-ink-mid" htmlFor="totp-code">
            Authenticator code
          </label>
          <input
            id="totp-code"
            type="text"
            inputMode="numeric"
            maxLength={6}
            value={totpCode}
            onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
            onKeyDown={(e) => { if (e.key === 'Enter') submitTotp(); }}
            className="w-full rounded-sm border border-border bg-panel px-3 py-2 text-sm font-mono tracking-widest text-ink-high focus:outline-none focus:ring-1 focus:ring-primary"
            autoFocus
            disabled={busy}
          />
          <button
            type="button"
            onClick={submitTotp}
            disabled={busy || totpCode.length !== 6}
            className="w-full rounded-sm bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary-strong disabled:opacity-50"
          >
            {busy ? 'Verifying…' : 'Verify'}
          </button>
        </div>
      )}

      {/* Error */}
      {phase === 'error' && (
        <div className="space-y-3">
          <p role="alert" className="text-sm text-destructive-strong">{errorMsg}</p>
          <button
            type="button"
            onClick={runInit}
            className="w-full rounded-sm bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary-strong"
          >
            Try again
          </button>
        </div>
      )}

      {/* Done (brief flash before parent takes over) */}
      {phase === 'done' && (
        <p className="text-sm text-[--color-status-ok] text-center">Verified.</p>
      )}

      {/* Cancel always available */}
      <div className="pt-1 border-t border-divider">
        <button
          type="button"
          onClick={onCancel}
          className="w-full text-center text-sm text-ink-low hover:text-ink-mid underline"
        >
          Cancel
        </button>
      </div>
    </ModalShell>
  );
}
